// Client-side image downscaling shared by the receipt logo and the loyalty
// card-appearance logo. Downscaling to a small data: URL keeps the
// app_settings payload small (logos are stored inline, not uploaded to a
// bucket). SVGs pass through untouched (vector stays crisp); raster images
// are drawn onto a canvas at maxWidth and re-encoded. PNG/WebP keep their
// alpha channel; everything else is flattened onto white as JPEG.
export async function resizeImageToDataUrl(file: File, maxWidth: number, quality: number): Promise<string> {
  // SVG: read as text and encode as data URL directly — stays crisp.
  if (file.type === 'image/svg+xml') {
    const text = await file.text()
    const encoded = window.btoa(unescape(encodeURIComponent(text)))
    return `data:image/svg+xml;base64,${encoded}`
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read file'))
    reader.onload = () => resolve(String(reader.result))
    reader.readAsDataURL(file)
  })

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = () => reject(new Error('Could not decode image'))
    el.src = dataUrl
  })

  const ratio = img.width > maxWidth ? maxWidth / img.width : 1
  const w = Math.round(img.width * ratio)
  const h = Math.round(img.height * ratio)

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not supported')
  // Transparent PNG support: preserve alpha when the source has it.
  const hasAlpha = file.type === 'image/png' || file.type === 'image/webp'
  if (!hasAlpha) {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, w, h)
  }
  ctx.drawImage(img, 0, 0, w, h)
  return hasAlpha ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', quality)
}
